/**
 * ErrorBoundary Component - Enhanced Version
 *
 * Catches JavaScript errors in child components and displays a fallback UI.
 * Prevents the entire app from crashing due to errors in one component.
 *
 * Features:
 * - Graceful error handling with user-friendly UI
 * - Error logging for debugging & analytics
 * - One-click recovery options
 * - Development mode: Shows error details
 * - Production mode: Clean error message
 * - Minimal mode for smaller components
 */

import React from 'react';
import { AlertTriangle, RefreshCw, Home, ChevronDown } from 'lucide-react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render shows the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    this.setState({ errorInfo });

    // Log to console
    console.error('ErrorBoundary caught an error:', error);
    console.error('Component stack:', errorInfo?.componentStack);

    // Log to analytics if available (Mixpanel, etc.)
    try {
      if (window.mixpanel && typeof window.mixpanel.track === 'function') {
        window.mixpanel.track('App Error', {
          error: error.toString(),
          componentStack: errorInfo?.componentStack?.substring(0, 500),
          url: window.location.href,
          timestamp: new Date().toISOString(),
        });
      }
    } catch (e) {
      // Silently fail if analytics not available
      console.warn('Failed to log error to analytics:', e);
    }
  }

  handleRefresh = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    });
  };

  toggleDetails = () => {
    this.setState(prev => ({ showDetails: !prev.showDetails }));
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo, showDetails } = this.state;
      const { fallback, fallbackMessage, minimal } = this.props;

      // If custom fallback component provided, use it
      if (fallback) {
        return fallback;
      }

      // Minimal error UI (for smaller components like cards, sections)
      if (minimal) {
        return (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-center">
            <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
            <p className="text-red-700 text-sm mb-2">
              {fallbackMessage || 'Something went wrong'}
            </p>
            <button
              onClick={this.handleRetry}
              className="inline-flex items-center text-sm text-red-600 hover:text-red-800 underline"
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              Try again
            </button>
          </div>
        );
      }

      // Full error UI (for page-level errors)
      return (
        <div className="min-h-[60vh] flex items-center justify-center bg-gray-50 px-4 py-8">
          <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* Header with gradient */}
            <div className="bg-gradient-to-r from-red-500 to-orange-500 px-6 py-8 text-center">
              <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">
                Oops! Something went wrong
              </h1>
              <p className="text-white/80 text-sm">
                We're sorry for the inconvenience
              </p>
            </div>

            {/* Content */}
            <div className="px-6 py-6">
              <p className="text-gray-600 text-center mb-6">
                {fallbackMessage ||
                  'An unexpected error occurred. You can try refreshing the page or go back to the home page.'}
              </p>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <button
                  onClick={this.handleRefresh}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-br from-[#FFD200] to-[#F7971E] text-white font-semibold rounded-xl hover:from-[#F7971E] hover:to-[#FFD200] transition-all shadow-md hover:shadow-lg"
                >
                  <RefreshCw className="w-5 h-5" />
                  Refresh Page
                </button>

                <button
                  onClick={this.handleGoHome}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-all"
                >
                  <Home className="w-5 h-5" />
                  Go Home
                </button>
              </div>

              {/* Retry Button */}
              <button
                onClick={this.handleRetry}
                className="w-full text-center text-sm text-gray-500 hover:text-gray-700 underline mb-4"
              >
                Or try to continue without refreshing
              </button>

              {/* Error Details (Development Only) */}
              {process.env.NODE_ENV === 'development' && error && (
                <div className="border-t border-gray-200 pt-4">
                  <button
                    onClick={this.toggleDetails}
                    className="w-full flex items-center justify-between text-sm text-gray-500 hover:text-gray-700 mb-2"
                  >
                    <span>Error Details (Dev Only)</span>
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${showDetails ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {showDetails && (
                    <div className="bg-gray-900 text-gray-100 rounded-lg p-4 text-xs font-mono overflow-auto max-h-48">
                      <div className="mb-2">
                        <span className="text-red-400">Error: </span>
                        {error.toString()}
                      </div>
                      {errorInfo?.componentStack && (
                        <div>
                          <span className="text-yellow-400">Component Stack:</span>
                          <pre className="mt-1 text-gray-400 whitespace-pre-wrap">
                            {errorInfo.componentStack}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 text-center">
              <p className="text-xs text-gray-500">
                If this problem persists, please{' '}
                <a href="/contact" className="text-[#F7971E] hover:underline">
                  contact our support team
                </a>
              </p>
            </div>
          </div>
        </div>
      );
    }

    // No error - render children normally
    return this.props.children;
  }
}

// HOC to wrap components with error boundary
export const withErrorBoundary = (Component, options = {}) => {
  const { fallbackMessage, minimal } = options;

  return function WrappedComponent(props) {
    return (
      <ErrorBoundary fallbackMessage={fallbackMessage} minimal={minimal}>
        <Component {...props} />
      </ErrorBoundary>
    );
  };
};

export default ErrorBoundary;
